import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import { Hotel, Star, MapPin, Wifi, Calendar, Users, CheckCircle2, Trash2, ChevronLeft, ChevronRight, Maximize2, X, ArrowRight, SkipForward } from "lucide-react";
import ClosedFeature from "@/components/ClosedFeature";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Dialog, DialogTrigger, DialogContent } from "@/components/ui/dialog";

function HotelImageGallery({ images, name, onExpand }) {
  const [index, setIndex] = useState(0);
  const pics = images?.length ? images : [];
  if (!pics.length) return null;

  const prev = (e) => { e.stopPropagation(); setIndex((i) => (i - 1 + pics.length) % pics.length); };
  const next = (e) => { e.stopPropagation(); setIndex((i) => (i + 1) % pics.length); };

  return (
    <div className="h-48 overflow-hidden relative group">
      <img
        src={pics[index]}
        alt={`${name} ${index + 1}`}
        className="w-full h-full object-cover cursor-zoom-in"
        onClick={(e) => { e.stopPropagation(); onExpand?.(pics, index); }}
      />
      <button
        onClick={(e) => { e.stopPropagation(); onExpand?.(pics, index); }}
        className="absolute top-1.5 right-1.5 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label="View full screen"
      >
        <Maximize2 className="w-3.5 h-3.5" />
      </button>
      {pics.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Previous photo">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={next} className="absolute right-1 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Next photo">
            <ChevronRight className="w-4 h-4" />
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {pics.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === index ? "bg-[#00F0FF]" : "bg-white/50"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ImageLightbox({ images, index, onClose, onIndexChange }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onIndexChange((index - 1 + images.length) % images.length);
      if (e.key === "ArrowRight") onIndexChange((index + 1) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onIndexChange]);

  return (
    <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="image-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2" aria-label="Close">
        <X className="w-6 h-6" />
      </button>
      <img src={images[index]} alt={`Photo ${index + 1}`} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onIndexChange((index - 1 + images.length) % images.length); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            aria-label="Previous photo"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onIndexChange((index + 1) % images.length); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            aria-label="Next photo"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-xs">{index + 1} / {images.length}</div>
        </>
      )}
    </div>
  );
}

export default function Accommodation() {
  const { user } = useAuth();
  const [hotels, setHotels] = useState([]);
  const [selected, setSelected] = useState(null);
  const [roomType, setRoomType] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [booking, setBooking] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [meta, setMeta] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [unpaidReg, setUnpaidReg] = useState(null);
  const [dashData, setDashData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();

  const openLightbox = (images, index) => setLightbox({ images, index });

  const isIndian = user?.nationality === "India";
  const currencySymbol = isIndian ? "₹" : "$";
  const priceOf = (rt) => isIndian ? rt.price_per_night_inr : rt.price_per_night_usd;

  useEffect(() => {
    api.get("/meta/events").then((r) => setMeta(r.data)).catch(() => { });
    api.get("/hotels").then((r) => {
      let h = r.data;
      if (user?.nationality !== "India") {
        h = h.filter(hotel => hotel.rating >= 5);
      }
      setHotels(h);
    }).catch(() => { });
    
    api.get("/dashboard/summary").then((r) => {
      setDashData(r.data);
      const regs = r.data?.registrations || [];
      setUnpaidReg(regs.find(rg => rg.payment_status !== "paid") || null);
      setDataLoaded(true);
    }).catch(() => { setDataLoaded(true); });

    api.get("/accommodations/me").then((r) => setMyBookings(r.data)).catch(() => { });
  }, [user]);

  if (meta && !meta.accommodation_open && user?.role !== "admin") {
    if (isIndian) {
      const sfiStatus = user?.sfi_results_status;
      return (
        <ClosedFeature
          featureName="Hotel Booking"
          icon={Hotel}
          subtitle={sfiStatus === "pending" ? "SFI RESULTS UNDER REVIEW" : sfiStatus === "rejected" ? "SFI RESULTS NOT APPROVED" : "SFI VERIFICATION REQUIRED"}
          description={
            sfiStatus === "pending"
              ? "Your SFI National Masters Results submission is under review. Hotel booking (optional) unlocks once it's verified."
              : sfiStatus === "rejected"
                ? "Your SFI National Masters Results submission was not approved. Re-upload it from your dashboard."
                : "Upload your SFI National Masters Results document from your dashboard — hotel booking is optional for Indian athletes, but unlocks once verified."
          }
        />
      );
    }
    return <ClosedFeature featureName="Hotel Booking" icon={Hotel} />;
  }

  if (!dataLoaded) {
    return (
      <div className="min-h-screen bg-[#0B1429]">
        <Navbar />
        <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-10 space-y-4">
          <div className="h-8 w-24 bg-white/[0.03] animate-pulse rounded" />
          <div className="h-12 w-64 bg-white/[0.03] animate-pulse rounded" />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-64 bg-white/[0.03] animate-pulse rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (dataLoaded && user?.role !== "admin") {
    const hasEventsInCart = !!unpaidReg;
    const hasPaidAcc = !!dashData?.accommodations?.some(a => a.payment_status === "paid");
    if (!hasEventsInCart && !hasPaidAcc && (dashData?.registrations || []).length === 0) {
      return (
        <ClosedFeature
          featureName="Hotel Booking"
          icon={Calendar}
          subtitle="SELECT EVENTS FIRST"
          badgeLabel={null}
          description="Please complete your event selection first. Once you've chosen your events, you'll be directed here to book your accommodation."
        />
      );
    }
  }


  const book = async () => {
    if (!selected || !roomType) { toast.error("Pick a hotel & room type"); return; }
    setBooking(true);
    try {
      const { data } = await api.post("/accommodations", {
        hotel_id: selected.hotel_id,
        room_type: roomType,
        check_in: checkIn,
        check_out: checkOut,
        guests: roomType.toLowerCase() === "double" ? 2 : 1,
        occupancy: roomType.toLowerCase()
      });
      if (unpaidReg) {
        toast.success("Accommodation added to cart. Proceeding to checkout...");
        navigate(`/payment?registration_id=${unpaidReg.registration_id}&booking_id=${data.booking_id}`);
      } else {
        toast.success(`Booking confirmed! ${data.booking_id} — pay from dashboard`);
        setMyBookings((p) => [data, ...p]);
        setSelected(null); setRoomType("");
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  const payBooking = async (booking_id) => {
    if (unpaidReg) {
      navigate(`/payment?registration_id=${unpaidReg.registration_id}&booking_id=${booking_id}`);
      return;
    }
    try {
      const result = await payWithRazorpay({ booking_id }, user);
      if (!result) return; // user dismissed the payment modal
      toast.success("Payment successful");
      const r = await api.get("/accommodations/me");
      setMyBookings(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatError(e.response.data.detail) : e.message);
    }
  };

  const deleteBooking = async (booking_id) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      await api.delete(`/accommodations/me/${booking_id}`);
      toast.success("Booking cancelled");
      setMyBookings((p) => p.filter(b => b.booking_id !== booking_id));
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-7xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-6">
          <div>
            <div className="eyebrow mb-3">Stay</div>
            <h1 className="font-display font-black text-4xl md:text-5xl mb-2">Partner <span className="text-[#00F0FF]">hotels</span> in Bengaluru</h1>
            <p className="text-[#94A3B8]">Championship-rate accommodation, all within minutes of the venue.</p>
          </div>
          <Dialog>
            <DialogTrigger asChild>
              <button className="flex items-center gap-4 bg-white/[0.03] border border-white/10 p-2 pr-5 rounded-full hover:bg-white/[0.06] hover:border-[#00F0FF]/30 transition-all text-left shrink-0 cursor-pointer shadow-lg mt-2 md:mt-0">
                 <div className="w-12 h-12 rounded-full overflow-hidden border border-[#00F0FF]/30 shrink-0">
                   <img src="/images/accommodation-manager.jpg" alt="Manager" className="w-full h-full object-cover object-top" />
                 </div>
                 <div>
                   <div className="text-[10px] text-[#00F0FF] font-bold uppercase tracking-[0.15em] mb-0.5">Need Help?</div>
                   <div className="text-sm font-semibold text-white">Contact Accommodation Manager</div>
                 </div>
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-[#142040] border border-white/10 p-0 overflow-hidden">
               <img src="/images/accommodation-manager.jpg" alt="Accommodation Manager Contact" className="w-full h-auto object-cover" />
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-10">
        {!isIndian && (
          <div className="card-dark p-6 mb-10 border-[#00F0FF]/20">
            <h2 className="font-display font-bold text-lg mb-4">Complete Hospitality Package Includes:</h2>
            <ul className="space-y-2.5">
              {[
                "Full-board accommodation, including breakfast, lunch and dinner",
                "Airport transfers",
                "Daily hotel-to-pool and pool-to-hotel transfers",
                "Exciting incentives and additional benefits throughout the programme",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-[#CBD5E1]">
                  <CheckCircle2 className="w-4 h-4 text-[#00F0FF] mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 border-t border-white/10 text-sm text-[#94A3B8]">
              Not staying at an official hotel? You can opt to <Link to={`/administrative-fee${unpaidReg ? `?cart_reg_id=${unpaidReg.registration_id}` : ""}`} className="text-[#00F0FF] hover:underline">pay the Administrative Fee instead</Link>.
            </div>
          </div>
        )}

        {isIndian && unpaidReg && (
          <div
            className="relative overflow-hidden rounded-2xl border border-[#00F0FF]/20 p-5 mb-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{ background: "linear-gradient(135deg, rgba(0,240,255,0.05) 0%, rgba(20,32,64,0.4) 100%)" }}
          >
            <div className="absolute -left-8 top-1/2 -translate-y-1/2 w-40 h-40 bg-[#00F0FF]/[0.05] blur-3xl rounded-full pointer-events-none" />
            <div className="relative flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-center shrink-0">
                <SkipForward className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00F0FF] mb-0.5">Optional</p>
                <p className="text-sm text-white font-medium">Accommodation isn't required — skip straight to payment if you don't need a hotel.</p>
              </div>
            </div>
            <Link
              to={`/payment/${unpaidReg.registration_id}`}
              className="relative shrink-0 h-9 px-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] text-xs font-bold hover:bg-[#00F0FF]/20 transition-colors inline-flex items-center gap-2 touch-manipulation"
            >
              Skip to Payment <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {myBookings.length > 0 && (
          <div className="mb-10">
            <div className="eyebrow mb-3">Your Bookings</div>
            <div className="grid md:grid-cols-2 gap-4">
              {myBookings.map((b) => (
                <div key={b.booking_id} className="card-dark p-5 flex justify-between items-center" data-testid={`booking-${b.booking_id}`}>
                  <div>
                    <div className="font-semibold pr-4">{b.hotel_name}</div>
                    {b.nights > 0 ? (
                      <>
                        <div className="text-xs text-[#94A3B8]">{b.room_type} · {b.nights} nights · {b.currency === "INR" ? "₹" : "$"}{b.total_amount_usd}</div>
                        <div className="text-xs text-[#94A3B8] mt-1">{b.check_in} → {b.check_out}</div>
                      </>
                    ) : (
                      <div className="text-xs text-[#94A3B8] mt-0.5">{b.currency === "INR" ? "₹" : "$"}{b.total_amount_usd}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {b.payment_status === "paid"
                      ? <>
                        <span className="text-[#00E676] text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />PAID</span>
                        <a href={`/invoice/accommodation/${b.booking_id}`} className="text-[#00F0FF] hover:underline text-xs">Download Invoice →</a>
                      </>
                      : <div className="flex gap-2">
                        <button onClick={() => deleteBooking(b.booking_id)} className="btn-secondary !py-2 !px-3 text-xs text-[#FF3B30] hover:text-[#FF3B30] hover:border-[#FF3B30]/30" title="Cancel Booking"><Trash2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => payBooking(b.booking_id)} className="btn-primary !py-2 !px-4 text-xs">Pay Now</button>
                      </div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hotels.map((h) => (
            <div key={h.hotel_id} className="card-dark overflow-hidden" data-testid={`hotel-${h.hotel_id}`}>
              <HotelImageGallery images={h.images?.length ? h.images : [h.image]} name={h.name} onExpand={openLightbox} />
              <div className="p-5">
                <div className="flex items-center gap-2 text-xs text-[#00F0FF] mb-1"><Star className="w-3 h-3 fill-current" /> {h.rating} · {h.distance_km} km from venue</div>
                <h3 className="font-display font-bold text-lg">{h.name}</h3>
                <p className="text-xs text-[#94A3B8] mt-1">{h.tagline}</p>
                <div className="flex flex-wrap gap-1 mt-3 mb-3">
                  {h.amenities.slice(0, 3).map((a) => (
                    <span key={a} className="text-[10px] bg-[#0B1429] border border-[#2A3A5F] px-2 py-0.5 rounded">{a}</span>
                  ))}
                </div>
                {h.room_types?.length > 0 ? (
                  <div className="text-sm text-[#94A3B8]">From <span className="text-[#00F0FF] font-bold text-base">{currencySymbol}{priceOf(h.room_types[0])}</span>/night</div>
                ) : (
                  <div className="text-sm text-[#94A3B8]">Pricing coming soon</div>
                )}
                <button
                  onClick={() => { setSelected(h); setRoomType(h.room_types?.[0]?.type || ""); }}
                  disabled={!h.room_types?.length}
                  className="btn-secondary w-full justify-center mt-4 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`select-hotel-${h.hotel_id}`}
                >
                  {h.room_types?.length ? "Select & Book" : "Pricing coming soon"}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Booking modal */}
        {selected && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
            <div className="bg-[#142040] border border-[#2A3A5F] rounded-xl max-w-lg w-full p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
              <div className="rounded-lg overflow-hidden -mx-7 -mt-7 mb-5">
                <HotelImageGallery images={selected.images?.length ? selected.images : [selected.image]} name={selected.name} onExpand={openLightbox} />
              </div>
              <h3 className="font-display font-bold text-2xl mb-1">{selected.name}</h3>
              <p className="text-xs text-[#94A3B8] mb-5">{selected.tagline}</p>
              <div className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Occupancy</label>
                  <select value={roomType} onChange={(e) => setRoomType(e.target.value)} className="input-dark" data-testid="room-type-select">
                    {selected.room_types.map((r) => <option key={r.type} value={r.type}>{r.type} — {currencySymbol}{priceOf(r)}/night</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Check-in</label>
                    <input type="date" value={checkIn} min="2026-10-01" max="2026-10-14" onChange={(e) => setCheckIn(e.target.value)} className="input-dark" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Check-out</label>
                    <input type="date" value={checkOut} min={checkIn || "2026-10-02"} max="2026-10-15" onChange={(e) => setCheckOut(e.target.value)} className="input-dark" />
                  </div>
                </div>

                <button onClick={book} disabled={booking} className="btn-primary w-full justify-center" data-testid="confirm-booking">
                  {booking ? "Booking..." : "Confirm Booking"}
                </button>
              </div>
            </div>
          </div>
        )}
        </div>
        {lightbox && (
          <ImageLightbox
            images={lightbox.images}
            index={lightbox.index}
            onClose={() => setLightbox(null)}
            onIndexChange={(i) => setLightbox((p) => ({ ...p, index: i }))}
          />
        )}
      </div>
    </div>
  );
}
